/**
 * localStorage utilities for API key persistence
 */

import {
  ensureExtension,
  hasValidExtension,
  removeExtension,
  getExtension,
} from "@/lib/utils/filename";
import type { ShareFormat } from "@core/types/share";

const API_KEY_STORAGE_KEY = "myrkup:apiKey";

/**
 * Get the stored API key from localStorage
 */
export function getStoredApiKey(): string | null {
  try {
    return localStorage.getItem(API_KEY_STORAGE_KEY);
  } catch {
    // localStorage may not be available (e.g., in private browsing mode)
    return null;
  }
}

/**
 * Store the API key in localStorage
 * Also adopts anonymous drafts so they don't accumulate across sign-in/sign-out cycles
 */
export function setStoredApiKey(apiKey: string): void {
  try {
    // Adopt anonymous drafts into the new account so they carry over
    // but won't reappear as anonymous when the user later signs out
    convertAnonymousDraftsToApiKey(apiKey);
    localStorage.setItem(API_KEY_STORAGE_KEY, apiKey);
  } catch {
    // localStorage may not be available
    console.warn("Failed to store API key in localStorage");
  }
}

/**
 * Remove the stored API key from localStorage
 */
export function clearStoredApiKey(): void {
  try {
    localStorage.removeItem(API_KEY_STORAGE_KEY);
  } catch {
    // localStorage may not be available
  }
}

/**
 * Check if an API key is stored
 */
export function hasStoredApiKey(): boolean {
  return getStoredApiKey() !== null;
}

/**
 * Share passcode storage - stores passcodes locally for share management
 */
const PASSCODES_STORAGE_KEY = "myrkup:passcodes";
const MANAGEMENT_TOKENS_STORAGE_KEY = "myrkup:managementTokens";

interface StoredPasscodes {
  [shareId: string]: string;
}

interface StoredManagementTokens {
  [shareId: string]: string;
}

/**
 * Get all stored passcodes
 */
function getStoredPasscodes(): StoredPasscodes {
  try {
    const data = localStorage.getItem(PASSCODES_STORAGE_KEY);
    return data ? JSON.parse(data) : {};
  } catch {
    return {};
  }
}

/**
 * Store a passcode for a share
 */
export function storeSharePasscode(shareId: string, passcode: string): void {
  try {
    const passcodes = getStoredPasscodes();
    passcodes[shareId] = passcode;
    localStorage.setItem(PASSCODES_STORAGE_KEY, JSON.stringify(passcodes));
  } catch {
    console.warn("Failed to store share passcode in localStorage");
  }
}

/**
 * Get the stored passcode for a share
 */
export function getSharePasscode(shareId: string): string | null {
  try {
    const passcodes = getStoredPasscodes();
    return passcodes[shareId] || null;
  } catch {
    return null;
  }
}

/**
 * Remove the stored passcode for a share
 */
export function clearSharePasscode(shareId: string): void {
  try {
    const passcodes = getStoredPasscodes();
    delete passcodes[shareId];
    localStorage.setItem(PASSCODES_STORAGE_KEY, JSON.stringify(passcodes));
  } catch {
    // localStorage may not be available
  }
}

/**
 * Management token storage - stores management tokens locally for editable shares
 */

/**
 * Get all stored management tokens
 */
function getStoredManagementTokens(): StoredManagementTokens {
  try {
    const data = localStorage.getItem(MANAGEMENT_TOKENS_STORAGE_KEY);
    return data ? JSON.parse(data) : {};
  } catch {
    return {};
  }
}

/**
 * Store a management token for a share
 */
export function storeManagementToken(shareId: string, token: string): void {
  try {
    const tokens = getStoredManagementTokens();
    tokens[shareId] = token;
    localStorage.setItem(MANAGEMENT_TOKENS_STORAGE_KEY, JSON.stringify(tokens));
  } catch {
    console.warn("Failed to store management token in localStorage");
  }
}

/**
 * Get the stored management token for a share
 */
export function getManagementToken(shareId: string): string | null {
  try {
    const tokens = getStoredManagementTokens();
    return tokens[shareId] || null;
  } catch {
    return null;
  }
}

/**
 * Remove the stored management token for a share
 */
export function clearManagementToken(shareId: string): void {
  try {
    const tokens = getStoredManagementTokens();
    delete tokens[shareId];
    localStorage.setItem(MANAGEMENT_TOKENS_STORAGE_KEY, JSON.stringify(tokens));
  } catch {
    // localStorage may not be available
  }
}

/**
 * Draft storage - stores drafts locally for offline editing
 */
const DRAFTS_STORAGE_KEY = "myrkup:drafts";

export interface Draft {
  id: string;
  filename: string;
  content: string;
  createdAt: number;
  updatedAt: number;
  apiKey?: string; // API key this draft belongs to (undefined for anonymous drafts)
  // Optional share settings - stored per draft
  securityOptions?: {
    expiresIn: string;
    burnAfterRead: boolean;
    isModifiable: boolean;
  };
  passcodeOptions?: {
    passcode: string;
    embedInUrl: boolean;
    useCustomPasscode: boolean;
    isPublic: boolean;
  };
  formatOptions?: {
    format: ShareFormat;
  };
}

/**
 * Generate a unique ID for a draft
 */
function generateDraftId(): string {
  return `draft_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Get a unique filename by appending (1), (2), etc. if the name already exists
 * Preserves file extensions when adding the counter
 */
export function getUniqueFilename(
  baseName: string,
  drafts: Draft[],
  excludeId?: string,
): string {
  const names = new Set(
    drafts.filter((d) => d.id !== excludeId).map((d) => d.filename),
  );
  if (!names.has(baseName)) return baseName;

  // Extract extension if present
  const ext = getExtension(baseName);
  const nameWithoutExt = ext ? removeExtension(baseName) : baseName;

  let counter = 1;
  const generateName = () =>
    ext
      ? `${nameWithoutExt} (${counter})${ext}`
      : `${nameWithoutExt} (${counter})`;

  while (names.has(generateName())) {
    counter++;
  }
  return generateName();
}

/**
 * Migrate old format values (e.g., 'txt') to supported formats
 */
function migrateFormat(format: string): ShareFormat {
  // 'txt' format is no longer supported, migrate to 'md'
  if (format === "txt") return "md";
  if (format === "md" || format === "mdc") return format;
  return "md"; // Default fallback
}

/**
 * Get all stored drafts from localStorage (unfiltered)
 * Migrates old format values to supported formats
 * @internal Use getDrafts() instead to get API-key-scoped drafts
 */
function getAllDraftsFromStorage(): Draft[] {
  try {
    const data = localStorage.getItem(DRAFTS_STORAGE_KEY);
    if (!data) return [];
    const drafts = JSON.parse(data) as Draft[];
    // Migrate any old 'txt' formats to 'md'
    return drafts.map((draft) => {
      if (draft.formatOptions?.format) {
        const migratedFormat = migrateFormat(draft.formatOptions.format);
        if (migratedFormat !== draft.formatOptions.format) {
          return {
            ...draft,
            formatOptions: { format: migratedFormat },
          };
        }
      }
      return draft;
    });
  } catch {
    return [];
  }
}

/**
 * Get all stored drafts for the current API key (or anonymous drafts if no API key)
 */
export function getDrafts(): Draft[] {
  try {
    const currentApiKey = getStoredApiKey();
    const allDrafts = getAllDraftsFromStorage();

    // Filter drafts by current API key
    // If user is signed in (has API key), show only drafts tagged with that key
    // If user is anonymous (no API key), show only drafts without an apiKey tag
    const filteredDrafts = allDrafts.filter((draft) => {
      if (currentApiKey) {
        return draft.apiKey === currentApiKey;
      } else {
        return !draft.apiKey;
      }
    });

    // Sort by createdAt descending (newest first, stable order)
    return filteredDrafts.sort((a, b) => b.createdAt - a.createdAt);
  } catch {
    return [];
  }
}

/**
 * Get a single draft by ID
 */
export function getDraft(id: string): Draft | null {
  try {
    const drafts = getDrafts();
    return drafts.find((d) => d.id === id) || null;
  } catch {
    return null;
  }
}

/**
 * Save a draft (create or update)
 * Automatically tags the draft with the current API key
 */
export function saveDraft(draft: Draft): void {
  try {
    const currentApiKey = getStoredApiKey();
    const allDrafts = getAllDraftsFromStorage();
    const existingIndex = allDrafts.findIndex((d) => d.id === draft.id);

    // Tag draft with current API key (or undefined for anonymous)
    const updatedDraft = {
      ...draft,
      apiKey: currentApiKey || undefined,
      updatedAt: Date.now(),
    };

    if (existingIndex >= 0) {
      allDrafts[existingIndex] = updatedDraft;
    } else {
      allDrafts.push(updatedDraft);
    }

    localStorage.setItem(DRAFTS_STORAGE_KEY, JSON.stringify(allDrafts));
    window.dispatchEvent(new Event("drafts-changed"));
  } catch {
    console.warn("Failed to save draft in localStorage");
  }
}

/**
 * Delete a draft by ID
 */
export function deleteDraft(id: string): void {
  try {
    const allDrafts = getAllDraftsFromStorage();
    const filtered = allDrafts.filter((d) => d.id !== id);
    localStorage.setItem(DRAFTS_STORAGE_KEY, JSON.stringify(filtered));
    window.dispatchEvent(new Event("drafts-changed"));
  } catch {
    // localStorage may not be available
  }
}

/**
 * Create a new draft with a unique filename
 * @param filename - The filename (with or without extension)
 * @param format - The format to use if filename has no extension (defaults to 'md')
 */
export function createDraft(
  filename: string = "untitled",
  format: ShareFormat = "md",
): Draft {
  // Ensure filename has an extension
  const filenameWithExt = hasValidExtension(filename)
    ? filename
    : ensureExtension(filename, format);

  const drafts = getDrafts();
  const uniqueFilename = getUniqueFilename(filenameWithExt, drafts);
  const now = Date.now();

  const draft: Draft = {
    id: generateDraftId(),
    filename: uniqueFilename,
    content: "",
    createdAt: now,
    updatedAt: now,
  };

  saveDraft(draft);
  return draft;
}

/**
 * Get the most recently updated draft, or null if none exist
 */
export function getCurrentDraft(): Draft | null {
  const drafts = getDrafts();
  if (drafts.length > 0) {
    return drafts[0]!; // Already sorted by updatedAt descending
  }
  return null;
}

/**
 * Get the most recently updated draft, or create one if none exist
 */
export function getOrCreateCurrentDraft(): Draft {
  return getCurrentDraft() ?? createDraft();
}

/**
 * Clear all empty drafts for the current API key
 * Empty drafts are those with no content (empty string or whitespace only)
 */
export function clearEmptyDrafts(): void {
  try {
    const currentApiKey = getStoredApiKey();
    const allDrafts = getAllDraftsFromStorage();

    // Filter out empty drafts for the current API key
    const filtered = allDrafts.filter((draft) => {
      // Only filter drafts belonging to current API key
      const belongsToCurrentKey = currentApiKey
        ? draft.apiKey === currentApiKey
        : !draft.apiKey;
      if (!belongsToCurrentKey) return true; // Keep drafts from other keys

      // Keep drafts with content
      return draft.content.trim().length > 0;
    });

    localStorage.setItem(DRAFTS_STORAGE_KEY, JSON.stringify(filtered));
    window.dispatchEvent(new Event("drafts-changed"));
  } catch {
    console.warn("Failed to clear empty drafts from localStorage");
  }
}

/**
 * Clear all drafts for a specific API key
 * If apiKey is null/undefined, clears all anonymous drafts
 */
export function clearDraftsForApiKey(apiKey: string | null): void {
  try {
    const allDrafts = getAllDraftsFromStorage();

    // Filter out drafts belonging to the specified API key
    const filtered = allDrafts.filter((draft) => {
      if (apiKey) {
        return draft.apiKey !== apiKey;
      } else {
        return draft.apiKey !== undefined;
      }
    });

    localStorage.setItem(DRAFTS_STORAGE_KEY, JSON.stringify(filtered));
    window.dispatchEvent(new Event("drafts-changed"));
  } catch {
    console.warn("Failed to clear drafts for API key from localStorage");
  }
}

/**
 * Convert anonymous drafts to a specific API key
 * Used when signing in to adopt any drafts created while anonymous
 */
export function convertAnonymousDraftsToApiKey(toApiKey: string): void {
  try {
    const allDrafts = getAllDraftsFromStorage();

    const converted = allDrafts.map((draft) => {
      if (!draft.apiKey) {
        return { ...draft, apiKey: toApiKey };
      }
      return draft;
    });

    localStorage.setItem(DRAFTS_STORAGE_KEY, JSON.stringify(converted));
    window.dispatchEvent(new Event("drafts-changed"));
  } catch {
    console.warn("Failed to convert anonymous drafts to API key");
  }
}

/**
 * Convert drafts from one API key to another
 * Used when signing out with "keep drafts" option to make them accessible anonymously
 */
export function convertDraftsToAnonymous(fromApiKey: string): void {
  try {
    const allDrafts = getAllDraftsFromStorage();

    // Convert all drafts from the specified API key to anonymous
    const converted = allDrafts.map((draft) => {
      if (draft.apiKey === fromApiKey) {
        return { ...draft, apiKey: undefined };
      }
      return draft;
    });

    localStorage.setItem(DRAFTS_STORAGE_KEY, JSON.stringify(converted));
    window.dispatchEvent(new Event("drafts-changed"));
  } catch {
    console.warn("Failed to convert drafts to anonymous");
  }
}

// ---------------------------------------------------------------------------
// Generic persistence helpers
// ---------------------------------------------------------------------------

/**
 * Load a clamped numeric value from localStorage.
 * Returns `defaultValue` when the key is missing or not a valid number.
 */
export function loadClampedNumber(
  key: string,
  defaultValue: number,
  min: number,
  max: number,
): number {
  if (typeof window === "undefined") return defaultValue;
  try {
    const saved = localStorage.getItem(key);
    if (saved === null) return defaultValue;
    const parsed = parseInt(saved, 10);
    return Number.isNaN(parsed)
      ? defaultValue
      : Math.min(max, Math.max(min, parsed));
  } catch {
    return defaultValue;
  }
}

/**
 * Persist a primitive value to localStorage under the given key.
 */
export function saveToLocalStorage(key: string, value: string | number): void {
  try {
    localStorage.setItem(key, String(value));
  } catch {
    // localStorage may not be available
  }
}

/**
 * Load a persisted Set<string> from localStorage (stored as JSON array).
 * Returns an empty set when the key is missing or invalid.
 */
export function loadPersistedSet(key: string): Set<string> {
  if (typeof window === "undefined") return new Set();
  try {
    const saved = localStorage.getItem(key);
    return saved ? new Set(JSON.parse(saved) as string[]) : new Set();
  } catch {
    return new Set();
  }
}

/**
 * Persist a Set<string> to localStorage as a JSON array.
 */
export function savePersistedSet(key: string, set: Set<string>): void {
  try {
    localStorage.setItem(key, JSON.stringify([...set]));
  } catch {
    // localStorage may not be available
  }
}
